<template>
  <div class="row">
    <div class="col-12">
      <LicensesTable :licenses="licenses">
        <template #appName="slotprops">
          <div>
            <div class="row">
              <div class="col-12">
                <h6
                  class="pointer"
                  role="link"
                  tabindex="0"
                  @keydown.enter="goToAppPage(slotprops.row.item)"
                  @click="goToAppPage(slotprops.row.item)"
                >
                  <strong>{{ slotprops.row.item.appName }}</strong>
                </h6>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="tooltip-container">
                  <p>{{ getAppTypeName(slotprops.row.item) }}</p>
                </div>
              </div>
            </div>
          </div>
          <!-- Old functionality: license option selection per app -->
          <div class="row mt-2">
            <div class="col-12 col-lg-7">
              <h6>Licenses</h6>

              <div class="tooltip-container">
                <p class="mb-0">
                  Mark license for install
                  <span
                    v-if="
                      slotprops.row.item.appSettings?.licenseVariantsAllowed &&
                      !slotprops.row.item.appSettings
                        ?.multiLicenseVariantSelection
                    "
                  >
                    |
                    <span
                      :class="
                        getSelectedLicenseIds(slotprops.row.item).length > 0
                          ? 'small font-phoenix-cyan pointer'
                          : 'small'
                      "
                      tabindex="0"
                      role="button"
                      @click.stop="clearSelectedLicenses(slotprops.row.item)"
                      @keydown.enter="clearSelectedLicenses(slotprops.row.item)"
                    >
                      Clear
                    </span>
                  </span>
                </p>
              </div>

              <BTable
                hover
                small
                responsive
                thead-class="d-none"
                borderless
                :sort-desc="true"
                :fields="licenseOptionTableFields"
                :items="
                  getGroupByLicenseOptionAndClientCount(slotprops.row.item)
                "
              >
                <template #cell(checkbox)="data">
                  <div v-if="data.item.appSettings?.licenseVariantsAllowed">
                    <div
                      v-if="data.item.appSettings?.multiLicenseVariantSelection"
                    >
                      <BFormCheckbox
                        v-model="
                          selectedLicenseIdsByAppId[slotprops.row.item.appId]
                        "
                        size="sm"
                        :value="data.item.id"
                      />
                    </div>
                    <div v-else>
                      <BFormRadio
                        v-model="
                          selectedLicenseIdsByAppId[slotprops.row.item.appId]
                        "
                        size="sm"
                        :value="[data.item.id]"
                      />
                    </div>
                  </div>

                  <div v-else>
                    <BFormCheckbox
                      v-model="
                        selectedLicenseIdsByAppId[slotprops.row.item.appId]
                      "
                      size="sm"
                      :value="data.item.id"
                    />
                  </div>
                </template>
                <template #cell(name)="data">
                  <div class="tooltip-container row">
                    <div class="col-md-10 col-12">
                      <span>{{
                        data.item.licenseOption?.name ?? "Unknown"
                      }}</span>
                      <div
                        v-if="
                          getBundleComponents(data.item.licenseOption).length >
                          0
                        "
                      >
                        <p class="mb-0">
                          (Also included:
                          <span
                            v-for="(
                              bundleComponent, index
                            ) in getBundleComponents(
                              data.item.licenseOption,
                            ).sort(sortBy({ key: 'name', order: 'asc' }))"
                            :key="bundleComponent.id"
                          >
                            {{ bundleComponent.name
                            }}{{
                              index <
                              getBundleComponents(data.item.licenseOption)
                                .length -
                                1
                                ? ", "
                                : ""
                            }}
                          </span>
                          )
                        </p>
                      </div>
                    </div>
                  </div>
                </template>

                <template #cell(available)="data">
                  <div class="tooltip-container">
                    <span class="light-gray">
                      {{ licensesLeft(data.item) }} license(s) available
                    </span>
                    <br />
                    <span v-if="data.item.clientCount !== 1" class="light-gray">
                      {{ data.item.clientCount }} Clients
                      <HelpBadgeCircle
                        class="d-inline"
                        tip-html="This license supports multiple clients (e.g. for a network license server)."
                      />
                    </span>
                  </div>
                </template>
                <template #cell(actions)="data">
                  <div class="tooltip-container">
                    <span
                      class="small font-phoenix-cyan pointer"
                      tabindex="0"
                      role="button"
                      @click.stop="moveToCompany(data.item)"
                      @keydown.enter="moveToCompany(data.item)"
                    >
                      Move
                    </span>
                  </div>
                </template>
              </BTable>
            </div>
            <div
              v-if="
                slotprops.row.item.appSettings &&
                slotprops.row.item.appSettings.appType &&
                slotprops.row.item.appSettings.appType.hasPlcInstallation
              "
              class="col-12 col-lg-4"
            >
              <h6>App</h6>
              <div class="tooltip-container">
                <p class="mb-0">Select the app version to be installed</p>
                <BFormCheckbox
                  v-model="
                    isAppInstallSelectedByAppId[slotprops.row.item.appId]
                  "
                  size="sm"
                  :disabled="
                    isAppInstallCheckBoxDisabled(slotprops.row.item.appId)
                  "
                  :class="
                    isAppInstallCheckBoxDisabled(slotprops.row.item.appId)
                      ? 'disable'
                      : ''
                  "
                >
                  <span>Install App Version:</span>
                </BFormCheckbox>
                <div :style="'display: flex !important;'">
                  <AppVersionSelect
                    :key="slotprops.row.item.appId"
                    :app-id="slotprops.row.item.appId"
                    :show-version-label="false"
                    :archived="true"
                    :deactivated="false"
                    @on-selected="
                      (v) => selectAppVersion(slotprops.row.item.appId, v)
                    "
                    @on-mounted="
                      (v) => selectAppVersion(slotprops.row.item.appId, v)
                    "
                  ></AppVersionSelect>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template #action="slotprops">
          <div>
            <div class="d-flex justify-content-end">
              <span
                v-if="showInstallAppWithoutLicenseNote(slotprops.row.item)"
                class="small"
                >Note: This will install the app <b>without</b> a license</span
              >
            </div>
            <div class="d-flex justify-content-end">
              <StoreButton
                id="install"
                variant="primary"
                :disabled="isInstallBtnDisabled(slotprops.row.item)"
                @click="handleInstallAppLicense(slotprops.row.item)"
              >
                Install
                <HelpBadgeCircle
                  v-if="isInstallBtnDisabled(slotprops.row.item)"
                  tip-html="Please select a license"
                />
              </StoreButton>
            </div>
          </div>
        </template>
        <template #empty>
          <div>
            <span>No License found.</span>
          </div>
        </template>
      </LicensesTable>
    </div>
  </div>
</template>
<script setup lang="ts">
import { type GroupedLicenses, useLicensesStore } from "~/stores/licensesstore";
import LicensesTable from "~/components/specific/licenses/LicensesTable.vue";
import groupBy from "~/helpers/groupBy";
import HelpBadgeCircle from "~/components/shared/buttons/HelpBadgeCircle.vue";
import type {
  WebModelGetAppLicenseOption,
  WebModelGetAppSettings,
  WebModelGetAppVersion,
  WebModelGetLicense,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import AppVersionSelect from "~/components/shared/inputfields/AppVersionSelect.vue";
import { useToastStore } from "~/stores/ui/toaststore";
import { sortBy } from "~/helpers/sortBy";
import { useGoToAppPage } from "~/mixins/useGoToAppPage";
import type { PropType } from "vue";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";

const licensesStore = useLicensesStore();
const licenses = computed(
  () => licensesStore.getGroupedLicensesForMyLicensePool,
);

type BundleComponent = { id: string | number; name: string };
type LicenseOptionWithBundleComponents = WebModelGetAppLicenseOption & {
  bundleComponents?: BundleComponent[];
};

const getBundleComponents = (
  option?: WebModelGetAppLicenseOption | null,
): BundleComponent[] => {
  return (
    (option as LicenseOptionWithBundleComponents | null | undefined)
      ?.bundleComponents ?? []
  );
};

const props = defineProps({
  handleInstall: {
    type: Function as PropType<
      (
        appSettings: WebModelGetAppSettings | undefined,
        selectedLicenses: WebModelGetLicense[],
        selectedAppVersionId: number | undefined,
      ) => void
    >,
    required: true,
  },
});

/**
 * Per-app selection, matching old behavior where selection lived inside each "row component".
 * Key: appId, Value: selected license ids (single or multiple depending on flags).
 */
const selectedLicenseIdsByAppId = reactive<Record<string, number[]>>({});
// per-row install checkbox state
const isAppInstallSelectedByAppId = reactive<Record<string, boolean>>({});
// per-row selected app version
const selectedAppVersionByAppId = reactive<
  Record<string, WebModelGetAppVersion | undefined>
>({});

const licenseOptionTableFields = [
  { key: "checkbox", label: "", tdClass: "w-10" },
  { key: "name", label: "Name", tdClass: "w-50" },
  { key: "available", label: "Available", tdClass: "w-25" },
  { key: "actions", label: "", tdClass: "w-15" },
];

const getGroupByLicenseOptionAndClientCount = (
  appLicense: GroupedLicenses,
): GroupedLicenses[] => {
  if (!appLicense?.allLicenses || appLicense.allLicenses.length < 1) return [];

  return groupBy(appLicense.allLicenses, (item) => {
    const optionId = item.licenseOption?.id ?? "no-license-option";
    const clientCount = item.clientCount ?? "no-client-count";
    return `licenseOptionId:${optionId}|clientCount:${clientCount}`;
  }).sort(sortBy({ key: "licenseOption.name", order: "asc" }));
};

watch(
  licenses,
  (newLicenses) => {
    for (const app of newLicenses ?? []) {
      if (!app?.appId) continue;

      if (!selectedLicenseIdsByAppId[app.appId])
        selectedLicenseIdsByAppId[app.appId] = [];
      if (isAppInstallSelectedByAppId[app.appId] === undefined)
        isAppInstallSelectedByAppId[app.appId] = false;
      if (selectedAppVersionByAppId[app.appId] === undefined)
        selectedAppVersionByAppId[app.appId] = undefined;

      const options = getGroupByLicenseOptionAndClientCount(app);
      if (options.length === 1) {
        const onlyId = options[0]?.id;
        if (typeof onlyId === "number")
          selectedLicenseIdsByAppId[app.appId] = [onlyId];
        else selectedLicenseIdsByAppId[app.appId] = [];
      }
    }
  },
  { immediate: true },
);

const getSelectedLicenseIds = (appLicense: GroupedLicenses): number[] => {
  if (!appLicense?.appId) return [];
  return selectedLicenseIdsByAppId[appLicense.appId] ?? [];
};

const clearSelectedLicenses = (appLicense: GroupedLicenses) => {
  if (!appLicense?.appId) return;
  selectedLicenseIdsByAppId[appLicense.appId] = [];
};

const showInstallAppWithoutLicenseNote = (appLicense: GroupedLicenses) => {
  const selectedLicenseIds = getSelectedLicenseIds(appLicense);
  const appId = appLicense.appId;
  return (
    !!appId &&
    isAppInstallSelectedByAppId[appId] === true &&
    (!selectedLicenseIdsByAppId[appId] || selectedLicenseIds.length < 1)
  );
};

const isInstallBtnDisabled = (appLicense: GroupedLicenses) => {
  const selected = getSelectedLicenseIds(appLicense);
  return (
    !selected ||
    (selected.length < 1 && !isAppInstallSelectedByAppId[appLicense.appId])
  );
};

const handleInstallAppLicense = (appLicense: GroupedLicenses) => {
  const selected = getSelectedLicenseIds(appLicense);
  console.debug("handleInstallAppLicense", {
    appId: appLicense.appId,
    appName: appLicense.appName,
    selectedLicenseIds: selected,
  });

  const selectedLicenses: WebModelGetLicense[] = [];
  licensesStore.userLicenses.find((license) => {
    if (license && license.id) {
      if (selected.includes(license.id)) {
        selectedLicenses.push(license);
      }
    }
  });

  const appSettings = appLicense.appSettings;
  if (!appSettings) {
    useToastStore().addToastWithMessageAndTitle(
      "Missing parameter",
      "No app settings found for app: " + appLicense.appName,
      "warning",
    );
    return;
  }
  let selectedAppVersion;
  if (isAppInstallSelectedByAppId[appLicense.appId] === true) {
    selectedAppVersion = selectedAppVersionByAppId[appLicense.appId];
  }
  props.handleInstall(appSettings, selectedLicenses, selectedAppVersion?.id);
};

const goToAppPage = async (appLicense: GroupedLicenses) => {
  await useGoToAppPage().goToAppPage(appLicense);
};

const moveToCompany = async (licenseGroup: GroupedLicenses) => {
  console.debug("moveToCompany", licenseGroup.licenseOption?.name);
  if (licenseGroup.id) {
    const success = await licensesStore.moveLicenseToCompany(licenseGroup.id);
    if (success) {
      useToastStore().addToastWithMessageAndTitle(
        "Success",
        "License moved to company",
        "success",
      );
    }
  }
};

const getAppTypeName = (groupedLicenses: GroupedLicenses) => {
  return groupedLicenses.appSettings?.appType?.name ?? "Unknown";
};

const isAppInstallCheckBoxDisabled = (appId: string | undefined) => {
  if (!appId) return true;
  return !selectedAppVersionByAppId[appId];
};

const licensesLeft = (licenseGroup: GroupedLicenses) => {
  // In the old UI this counted "left" from the (filtered) pool.
  // In this grouped view, the group already represents "available" items.
  return licenseGroup.allLicenses?.length ?? 0;
};

const selectAppVersion = (
  appId: string | undefined,
  appVersion: WebModelGetAppVersion | undefined,
) => {
  if (!appId) return;
  selectedAppVersionByAppId[appId] = appVersion;

  // optional: if you want to auto-uncheck when version gets cleared
  if (!appVersion) isAppInstallSelectedByAppId[appId] = false;
};
</script>

<style scoped lang="scss">
@use "@/sass/components/LicenseTable.scss" as *;
</style>
